import { useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import Button from '@/components/Button.tsx';
import { TypoBody, TypoCallout, TypoCaption, TypoFootnote } from '@/components/Typography.tsx';
import LADDER_MY_MATCHES from '@/graphql/queries/ladder-my-matches.graphql';
import LADDER_QUESTION_VAULT from '@/graphql/queries/ladder-question-vault.graphql';

import {
  LadderMatch, LadderVaultItem, MatchMode, MatchStatus, Tone, VaultGroup, MATCH_MODE_LABELS, TONE_TEXT, formatDate,
  formatMs, getMatchSides, getReviewPath, useLadderProfile, useMyClientId,
} from './common.ts';
import { Badge, EmptyState, InlineLoader, Modal, Panel, PlayerAvatar, ReviewLink, Table } from './components.tsx';

const PAGE_SIZE = 20;

const FILTERS: { label: string; mode?: MatchMode }[] = [
  { label: 'All' },
  { label: 'Ranked', mode: 'LADDER' },
  { label: 'Friendly', mode: 'FRIENDLY' },
  { label: 'Tournament', mode: 'TOURNAMENT' },
];

const STATUS_LABELS: Partial<Record<MatchStatus, string>> = {
  ACTIVE: 'In progress',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  WITHDRAWN: 'Withdrawn',
  CANCELLED: 'Cancelled',
};

const getOutcome = (match: LadderMatch, myId: number): { label: string; tone: Tone } => {
  if (match.matchStatus !== 'COMPLETED') {
    const label = STATUS_LABELS[match.matchStatus] ?? match.matchStatus;
    return { label, tone: match.matchStatus === 'ACTIVE' ? 'info' : 'neutral' };
  }
  if (!match.winnerId) return { label: 'No winner', tone: 'neutral' };
  return match.winnerId === myId ? { label: 'Victory', tone: 'success' } : { label: 'Defeat', tone: 'danger' };
};

const LadderHeadToHead = () => {
  const myId = useMyClientId();
  const { stats } = useLadderProfile();
  const [mode, setMode] = useState<MatchMode>();
  // Kept after closing so the modal keeps its content while it fades out.
  const [selected, setSelected] = useState<LadderMatch | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // How many rows were loaded when a page came back short; with a full last page that's the only sign of the end.
  const [endAt, setEndAt] = useState<number | null>(null);
  const { data, loading, fetchMore } = useQuery(LADDER_MY_MATCHES, {
    variables: { mode, limit: PAGE_SIZE, offset: 0 },
  });

  const fetched: LadderMatch[] = data?.ladderMyMatches ?? [];
  const hasMore = fetched.length > 0 && fetched.length % PAGE_SIZE === 0 && endAt !== fetched.length;

  const currentMode = useRef(mode);
  currentMode.current = mode;

  const loadMore = async () => {
    const requestedMode = mode;
    setLoadingMore(true);
    try {
      const { data: page } = await fetchMore({ variables: { offset: fetched.length } });
      const count = page?.ladderMyMatches?.length ?? 0;
      // A page for a filter the user has since left says nothing about where the current list ends.
      if (currentMode.current === requestedMode && count < PAGE_SIZE) setEndAt(fetched.length + count);
    }
    catch {
      // The error link has already shown the error; the button stays so the user can try again.
    }
    finally {
      setLoadingMore(false);
    }
  };
  // Pending challenges aren't history yet; they're answered in the app.
  const matches = fetched.filter(match => match.matchStatus !== 'PENDING');

  return (
    <Panel
      title="Head-to-head match history"
      action={stats && (
        <div className="flex gap-2">
          <Badge tone="success">{stats.wins} wins</Badge>
          <Badge tone="danger">{stats.losses} losses</Badge>
        </div>
      )}
    >
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map(filter => (
          <button
            key={filter.label}
            onClick={() => { setMode(filter.mode); setEndAt(null); }}
            className={`px-3 py-1 rounded-sm text-footnote font-semibold
              ${mode === filter.mode
            ? 'bg-neutral2-canvas2 text-neutral2-canvas2-text'
            : 'bg-shade-5 hover:bg-shade-10'}`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {loading && !fetched.length ? <InlineLoader /> : !matches.length ? (
        <EmptyState text="No head-to-head matches yet." />
      ) : (
        <>
          <Table
            head={[
              { label: 'Opponent' },
              { label: 'Type', className: 'text-center' },
              { label: 'Score', className: 'text-center' },
              { label: 'Outcome', className: 'text-center' },
              { label: 'Date' },
              { label: '', className: 'text-right' },
            ]}
          >
            {matches.map(match => {
              const { them, myScore, theirScore } = getMatchSides(match, myId);
              const outcome = getOutcome(match, myId);

              return (
                <tr key={match.ladderMatchId} className="hover:bg-shade-5">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3 min-w-[9rem]">
                      <PlayerAvatar player={them} size="sm" />
                      <TypoCallout className="font-semibold text-neutral2-text-strong">
                        {them?.ladderName ?? 'Unknown player'}
                      </TypoCallout>
                      {them?.rank && <Badge>Rank {them.rank}</Badge>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center whitespace-nowrap text-footnote text-neutral2-text-softer">
                    {MATCH_MODE_LABELS[match.matchMode]}
                  </td>
                  <td className={`px-3 py-3 text-center font-semibold whitespace-nowrap ${TONE_TEXT[outcome.tone]}`}>
                    {myScore} – {theirScore}
                  </td>
                  <td className="px-3 py-3 text-center"><Badge tone={outcome.tone}>{outcome.label}</Badge></td>
                  <td className="px-3 py-3 whitespace-nowrap text-neutral2-text-softer">
                    {formatDate(match.completedAt ?? match.updated)}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button
                      onClick={() => { setSelected(match); setDetailsOpen(true); }}
                      className="text-callout font-semibold text-primary hover:underline whitespace-nowrap"
                    >
                      Details →
                    </button>
                  </td>
                </tr>
              );
            })}
          </Table>
          {hasMore && (
            <div className="flex justify-center mt-4">
              <Button color="secondary" loading={loadingMore} onClick={loadMore}>
                Load more
              </Button>
            </div>
          )}
        </>
      )}
      {selected && (
        <MatchDetails match={selected} myId={myId} open={detailsOpen} onClose={() => setDetailsOpen(false)} />
      )}
    </Panel>
  );
};

const MatchDetails = ({ match, myId, open, onClose }: {
  match: LadderMatch;
  myId: number;
  open: boolean;
  onClose: () => void;
}) => {
  const { them, myScore, theirScore, rounds } = getMatchSides(match, myId);
  const outcome = getOutcome(match, myId);
  const reviewItems = useMatchReviewItems(match, open);

  return (
    <Modal open={open} title={`${MATCH_MODE_LABELS[match.matchMode]} match`} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-center gap-6 py-4 rounded bg-neutral2">
          <div className="text-center">
            <strong className="block text-title text-neutral2-text-strong">{myScore}</strong>
            <TypoCaption className="uppercase font-semibold text-neutral2-text-softer">You</TypoCaption>
          </div>
          <span className="text-heading text-neutral2-text-weak">–</span>
          <div className="text-center">
            <strong className="block text-title text-neutral2-text-strong">{theirScore}</strong>
            <TypoCaption className="uppercase font-semibold text-neutral2-text-softer">
              {them?.ladderName ?? 'Opponent'}
            </TypoCaption>
          </div>
        </div>
        <div className="text-center">
          <Badge tone={outcome.tone} className="text-callout">{match.endTitle ?? outcome.label}</Badge>
          {match.endMessage && <TypoBody className="mt-2 text-neutral-text">{match.endMessage}</TypoBody>}
        </div>
        <div className="flex flex-col gap-2">
          <TypoFootnote className="uppercase font-semibold text-neutral-text-strong">Round results</TypoFootnote>
          {rounds.length ? rounds.map(round => (
            <div key={round.roundNumber} className="flex flex-col gap-1 px-3 py-2 rounded-sm bg-neutral2 text-callout">
              <div className="flex justify-between">
                <TypoCaption className="uppercase font-semibold text-neutral2-text-softer">
                  Round {round.roundNumber}
                </TypoCaption>
                <div className="flex items-center gap-2">
                  {round.resolved && (
                    <Badge tone={round.won ? 'success' : round.lost ? 'danger' : 'neutral'}>
                      {round.won ? 'Won' : round.lost ? 'Lost' : 'Tied'}
                    </Badge>
                  )}
                  <RoundReviewLink item={reviewItems.get(round.examQuestionId)} />
                </div>
              </div>
              <AnswerLine
                label="You"
                correct={round.myCorrect}
                elapsedMs={round.myElapsedMs}
                timedOut={round.timedOut}
              />
              <AnswerLine
                label={them?.ladderName ?? 'Opponent'}
                correct={round.theirCorrect}
                elapsedMs={round.theirElapsedMs}
              />
            </div>
          )) : (
            <TypoCaption className="text-neutral-text-softer">No rounds were played.</TypoCaption>
          )}
        </div>
      </div>
    </Modal>
  );
};

/**
 * The viewer's answered questions from this match, keyed by question. Matches don't carry the attempt ids the
 * review page needs, so they're looked up in the Question Vault (which holds the latest attempt of each question).
 */
const useMatchReviewItems = (match: LadderMatch, open: boolean) => {
  const { data } = useQuery(LADDER_QUESTION_VAULT, { skip: !open });
  const examIds = new Set([match.setExamId, match.r4ExamId, match.r5ExamId]);
  const items = new Map<number, LadderVaultItem>();

  (data?.ladderQuestionVault?.groups ?? []).forEach((group: VaultGroup) => group.categories.forEach(category => {
    category.items
      .filter(item => examIds.has(item.examId))
      .forEach(item => items.set(item.examQuestionId, item));
  }));

  return items;
};

const RoundReviewLink = ({ item }: { item?: LadderVaultItem }) => (
  item ? <ReviewLink to={getReviewPath(item)} /> : null
);

const AnswerLine = ({ label, correct, elapsedMs, timedOut = false }: {
  label: string;
  correct: boolean | null;
  elapsedMs: number | null;
  timedOut?: boolean;
}) => {
  const tone: Tone = correct === null ? 'neutral' : correct ? 'success' : 'danger';
  let result = 'Waiting';
  if (timedOut) result = 'Timed out';
  else if (correct !== null) result = `${correct ? '✓' : '✗'} ${formatMs(elapsedMs)}`;

  return (
    <div className="flex justify-between">
      <span className="text-neutral2-text-soft">{label}</span>
      <span className={`font-semibold ${TONE_TEXT[timedOut ? 'danger' : tone]}`}>{result}</span>
    </div>
  );
};

export default LadderHeadToHead;
